import emptyPropertiesUrl from "@/assets/empty-properties.svg";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getLicenseTypesOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { filterAvailableLicenseTypes } from "@/lib/license-type-utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlusCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { DashboardHeader } from "./dashboard-header";

export function EmptyDashboard() {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const { data: licenseTypesData, isLoading: areLicenseTypesLoading } =
    useQuery({
      ...getLicenseTypesOptions(),
      enabled: isAuthSetupComplete,
    });

  // Filter out license types that are under development or deleted
  const licenseTypes = filterAvailableLicenseTypes(
    licenseTypesData?.license_types,
  );

  // Adjust button widths to match the widest one
  useEffect(() => {
    if (buttonsContainerRef.current && licenseTypes.length > 1) {
      const buttons = buttonsContainerRef.current.querySelectorAll("button");
      let maxWidth = 0;

      // First pass: measure all buttons in their natural width
      for (const button of buttons) {
        button.style.width = "auto"; // Reset to natural width
        const width = button.getBoundingClientRect().width;
        maxWidth = Math.max(maxWidth, width);
      }

      // Second pass: set all buttons to the max width
      for (const button of buttons) {
        button.style.width = `${maxWidth}px`;
      }
    }
  }, [licenseTypes]);

  return (
    <div className="w-full min-w-[375px] space-y-6">
      <DashboardHeader showRegisterButton={false} />
      <div className="flex flex-col container mx-auto max-w-3xl">
        <Card className="text-center">
          <CardContent className="text-center space-y-6">
            <img
              src={emptyPropertiesUrl}
              alt="Empty properties illustration"
              className="w-64 h-48 mx-auto mb-4"
            />
            <h3 className="text-2xl font-semibold tracking-tight">
              Welcome to Your Dashboard
            </h3>
            <p className="text-muted-foreground">
              You haven't registered any properties yet. Get started by
              registering your first property.
            </p>
            {!areLicenseTypesLoading && (
              <div
                ref={buttonsContainerRef}
                className={
                  licenseTypes.length === 1
                    ? "flex justify-center"
                    : "grid grid-cols-1 sm:grid-cols-2 gap-3 justify-items-center max-w-2xl mx-auto"
                }
              >
                {licenseTypes.map((licenseType) => (
                  <Link
                    key={licenseType.id}
                    to="/registrant/new-license"
                    search={{
                      licenseTypeId: licenseType.id,
                      jurisdictionId: undefined,
                    }}
                  >
                    <Button size="lg" className="h-12 px-8">
                      <PlusCircle className="h-5 w-5 mr-2" />
                      Register {licenseType.name}
                    </Button>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
